import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { Description, PlayArrow, Download, ContentCopy, Check } from '@mui/icons-material';
import { CallLog, CampaignContact } from '../../store/api/outboundCallingApi';
import { getApiUrl } from '../../config/api';
import { RootState } from '../../store';

interface CallLogsTabProps {
  callLogs: CallLog[];
  isLoading: boolean;
  contacts: CampaignContact[];
  campaignId: number;
}

const CallLogsTab: React.FC<CallLogsTabProps> = ({ callLogs, isLoading, contacts, campaignId }) => {
  const [selectedCallLog, setSelectedCallLog] = useState<CallLog | null>(null);
  const [transcriptDialogOpen, setTranscriptDialogOpen] = useState(false);
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
  const [copiedCallId, setCopiedCallId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const token = useSelector((state: RootState) => state.auth.token);

  const getContactName = (contactId: number): string => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      return `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown Contact';
    }
    return `Contact ${contactId}`;
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'pending':
      case 'in_progress':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number | undefined) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleTranscriptClick = (callLog: CallLog) => {
    setSelectedCallLog(callLog);
    setTranscriptDialogOpen(true);
  };

  const handleRecordingClick = (callLog: CallLog) => {
    setSelectedCallLog(callLog);
    setRecordingDialogOpen(true);
  };

  const handleCallIdCopy = async (callId: string) => {
    try {
      await navigator.clipboard.writeText(callId);
      setCopiedCallId(callId);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy call ID:', err);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (callLogs.length === 0) {
    return (
      <Alert severity="info">
        No call logs found for this campaign.
      </Alert>
    );
  }

  const handleDownloadCSV = async () => {
    try {
      if (!token) {
        alert('Please log in to download CSV');
        return;
      }

      const response = await fetch(
        getApiUrl(`/api/v1/outbound-calling/campaigns/${campaignId}/call-logs/csv`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      console.log('CSV download response status:', response.status, response.statusText);
      console.log('CSV download response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Failed to download CSV';
        try {
          const errorText = await response.text();
          console.error('Error response text:', errorText);
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          } catch {
            errorMessage = errorText || response.statusText || errorMessage;
          }
        } catch {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        console.error('CSV download error:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage
        });
        throw new Error(errorMessage);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'call_logs.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Get response as text first to debug
      const responseText = await response.text();
      console.log('CSV response text length:', responseText.length);
      console.log('CSV response text preview (first 500 chars):', responseText.substring(0, 500));
      console.log('CSV response text (full):', responseText);
      
      // Check if response is actually CSV or an error (check for JSON error format)
      if (responseText.trim().startsWith('{') && (responseText.includes('"error"') || responseText.includes('"detail"'))) {
        console.error('CSV response appears to be a JSON error:', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.detail || errorData.error || 'Invalid CSV response from server');
        } catch (e) {
          throw new Error('Invalid CSV response from server');
        }
      }
      
      // Check if response is just "null" as a string
      if (responseText.trim() === 'null' || responseText.trim() === '') {
        console.error('CSV response is null or empty:', responseText);
        throw new Error('CSV file is empty. No call logs found for this campaign.');
      }

      // Create blob from text
      const blob = new Blob([responseText], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      const errorMessage = error?.message || 'Failed to download CSV. Please try again.';
      alert(errorMessage);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Call Logs ({callLogs.length} calls)
        </Typography>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownloadCSV}
          disabled={callLogs.length === 0}
          color="primary"
        >
          Download CSV
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Contact Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Attempt</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Call ID</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {callLogs.map((callLog) => (
              <TableRow key={callLog.id}>
                <TableCell>
                  {getContactName(callLog.contact_id)}
                </TableCell>
                <TableCell>{callLog.phone_number}</TableCell>
                <TableCell>{callLog.attempt_number}</TableCell>
                <TableCell>
                  <Chip
                    label={callLog.call_status?.toUpperCase() || 'UNKNOWN'}
                    color={getStatusColor(callLog.call_status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(callLog.call_started_at)}</TableCell>
                <TableCell>{formatDuration(callLog.call_duration_seconds)}</TableCell>
                <TableCell>
                  {callLog.synthflow_call_id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                        onClick={() => handleCallIdCopy(callLog.synthflow_call_id!)}
                      >
                        {callLog.synthflow_call_id.substring(0, 8)}...
                      </Typography>
                      <Tooltip title={copiedCallId === callLog.synthflow_call_id ? "Copied!" : "Click to copy"}>
                        <IconButton
                          size="small"
                          onClick={() => handleCallIdCopy(callLog.synthflow_call_id!)}
                        >
                          {copiedCallId === callLog.synthflow_call_id ? <Check /> : <ContentCopy />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    'N/A'
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleTranscriptClick(callLog)}
                    title="View Transcript"
                    disabled={!callLog.transcript}
                  >
                    <Description />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRecordingClick(callLog)}
                    title="View Recording"
                    disabled={!callLog.recording_url}
                  >
                    <PlayArrow />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Transcript Dialog */}
      <Dialog
        open={transcriptDialogOpen}
        onClose={() => setTranscriptDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Call Transcript - {selectedCallLog ? getContactName(selectedCallLog.contact_id) : 'Unknown'}
        </DialogTitle>
        <DialogContent>
          {selectedCallLog?.transcript ? (
            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {selectedCallLog.transcript}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No transcript available for this call.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranscriptDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recording Dialog */}
      <Dialog
        open={recordingDialogOpen}
        onClose={() => setRecordingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Call Recording - {selectedCallLog ? getContactName(selectedCallLog.contact_id) : 'Unknown'}
        </DialogTitle>
        <DialogContent>
          {selectedCallLog?.recording_url ? (
            <Box>
              <audio controls style={{ width: '100%' }}>
                <source src={selectedCallLog.recording_url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
              <Box mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  href={selectedCallLog.recording_url}
                  download
                >
                  Download Recording
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">
              No recording available for this call.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordingDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Call ID copied to clipboard!"
      />
    </Box>
  );
};

export default CallLogsTab;
